/**
 * Hook multi-univers — gère la sélection et le filtrage par univers.
 * Chaque tenant peut avoir N univers (boutique, restaurant, bar, etc.)
 * Les produits/catégories sont filtrés par l'univers actif.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useEffectiveTenantId } from '@/hooks/useTenant';
import { useState, useCallback, createContext, useContext } from 'react';

export interface Universe {
  id: string;
  tenant_id: string;
  slug: string;
  label: string;
  icon: string;
  color: string;
  sort_order: number;
  is_active: boolean;
}

// ─── Context pour l'univers actif ─────────────────────────────
interface UniverseContextValue {
  universes: Universe[];
  activeUniverse: Universe | null;
  setActiveUniverse: (u: Universe | null) => void;
  isLoading: boolean;
}

const UniverseContext = createContext<UniverseContextValue>({
  universes: [],
  activeUniverse: null,
  setActiveUniverse: () => {},
  isLoading: false,
});

export const useUniverseContext = () => useContext(UniverseContext);
export { UniverseContext };

// ─── Hook de données ──────────────────────────────────────────
export function useUniverses() {
  const tenantId = useEffectiveTenantId();
  const queryClient = useQueryClient();

  const { data: universes = [], isLoading } = useQuery({
    queryKey: ['universes', tenantId],
    queryFn: async () => {
      if (!tenantId) return [];
      const { data, error } = await supabase
        .from('tenant_universes' as any)
        .select('*')
        .eq('tenant_id', tenantId)
        .eq('is_active', true)
        .order('sort_order');
      // Gracefully handle missing table — feature not yet deployed
      if (error) {
        if (
          error.code === '42P01' ||
          error.code?.startsWith('PGRST') ||
          error.message?.includes('does not exist') ||
          error.message?.includes('schema cache')
        )
          return [];
        throw error;
      }
      return (data || []) as unknown as Universe[];
    },
    enabled: !!tenantId,
  });

  const createUniverse = useMutation({
    mutationFn: async (input: { slug: string; label: string; icon?: string; color?: string }) => {
      if (!tenantId) throw new Error('No tenant');
      const { data, error } = await supabase
        .from('tenant_universes' as any)
        .insert({ tenant_id: tenantId, ...input })
        .select()
        .single();
      if (error) throw error;
      return data as unknown as Universe;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['universes'] }),
  });

  const updateUniverse = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Universe> & { id: string }) => {
      if (!tenantId) throw new Error('Tenant introuvable');
      const { error } = await supabase
        .from('tenant_universes' as any)
        .update(updates)
        .eq('id', id)
        .eq('tenant_id', tenantId);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['universes', tenantId] }),
  });

  const deleteUniverse = useMutation({
    mutationFn: async (id: string) => {
      if (!tenantId) throw new Error('Tenant introuvable');
      const { error } = await supabase
        .from('tenant_universes' as any)
        .update({ is_active: false })
        .eq('id', id)
        .eq('tenant_id', tenantId);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['universes', tenantId] }),
  });

  return {
    universes,
    isLoading,
    createUniverse,
    updateUniverse,
    deleteUniverse,
  };
}

// ─── Hook pour filtrer par univers actif ──────────────────────
export function useUniverseFilter() {
  const { activeUniverse } = useUniverseContext();

  /** Filtre SQL: retourne la clause pour universe_id */
  const filterByUniverse = useCallback(
    <T extends { universe_id?: string | null }>(items: T[]): T[] => {
      if (!activeUniverse) return items; // Pas d'univers actif → tout afficher
      return items.filter((item) => !item.universe_id || item.universe_id === activeUniverse.id);
    },
    [activeUniverse],
  );

  return { activeUniverseId: activeUniverse?.id || null, filterByUniverse };
}
